import { ReactNode } from "react";
import { usePositionsPanelContext } from "../position-panel-context";
import {
  DescriptionExternalLink,
  StaticDescription,
  TooltipDescription,
} from "../../description";
import { useDescriptionVariant } from "../../description/use-description-variant";
import { Button } from "../../button";
import { Link } from "../../link";

interface ActionButtonGroupProps {
  children?: ReactNode;
}

export const ActionButtonGroup = ({
  children,
}: ActionButtonGroupProps): JSX.Element => {
  const { positions, useIsPaused } = usePositionsPanelContext();
  const isPaused = useIsPaused(positions.siloAddress);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2.5 lg:gap-8">{children}</div>
      {isPaused && (
        <StaticDescription>
          <PausedDescription />
        </StaticDescription>
      )}
    </div>
  );
};

interface ActionButtonProps {
  href: string;
  ["data-cy"]?: string;
  children?: ReactNode;
}
export const ActionButton = ({
  href,
  "data-cy": dataCy,
  children,
}: ActionButtonProps): JSX.Element => {
  const { positions, useIsPaused } = usePositionsPanelContext();
  const isPaused = useIsPaused(positions.siloAddress);
  const descriptionVariant = useDescriptionVariant();

  if (isPaused && descriptionVariant === "static") {
    return (
      <Button
        className="w-fit"
        variant="action"
        disabled
        size="small"
        data-cy={dataCy}
      >
        {children}
      </Button>
    );
  }

  if (isPaused && descriptionVariant === "tooltip") {
    return (
      <TooltipDescription
        placement="bottom-start"
        trigger={
          <Button
            className="w-fit"
            variant="action"
            disabled
            size="small"
            data-cy={dataCy}
          >
            {children}
          </Button>
        }
      >
        <PausedDescription />
      </TooltipDescription>
    );
  }

  return (
    <Link
      data-cy={dataCy}
      href={href}
      variant="action"
      size="small"
      className="w-fit"
    >
      {children}
    </Link>
  );
};

const PausedDescription = (): JSX.Element => {
  const { links } = usePositionsPanelContext();

  return (
    <div className="flex flex-col gap-5">
      <p>This action is temporarily disabled while the silo is frozen.</p>

      <div>
        <DescriptionExternalLink
          className="inline-block lg:text-base"
          variant="action"
          href={links.pausedSilo}
        />
      </div>
    </div>
  );
};
